import { useEffect, useState } from 'react';
import * as Icon from './Icons';
import { initials, nip as formatNip } from '../format';
import type { Company } from '../api';
import { localeNames, locales, type Dictionary, type Locale } from '../i18n';

/**
 * Powłoka aplikacji: boczna nawigacja, okruszki, treść ekranu.
 *
 * Zastępuje osiemnaście przycisków pod kartą firmy, które nie mówiły, gdzie się jest —
 * potrafiło świecić pięć naraz. Nawigacja jest **pionowa i pogrupowana**, bo tych miejsc
 * jest osiemnaście i poziomo się nie mieszczą, a nazwa firmy przestaje być treścią strony
 * i staje się nagłówkiem kontekstu.
 */

export type Panel =
  | 'dashboard'
  | 'invoices'
  | 'settlements'
  | 'counterparties'
  | 'products'
  | 'costs'
  | 'documents'
  | 'mailboxConfig'
  | 'ledger'
  | 'ryczalt'
  | 'assets'
  | 'inventory'
  | 'periods'
  | 'annual'
  | 'billing'
  | 'settings'
  | 'access'
  | 'numbering'
  | 'chart'
  | 'journal'
  | 'reports'
  | 'bank';

type Entry = {
  panel: Panel;
  label: (t: Dictionary) => string;
  icon: React.ComponentType<{ className?: string }>;
  /** Ekran wyłącznie dla pełnych ksiąg; przy KPiR i ryczałcie nie ma czego na nim robić. */
  onlyFullBooks?: boolean;
  /**
   * Role, które ten ekran w ogóle widzą. Puste znaczy „każdy, kto ma dostęp do firmy".
   *
   * Wartości są przepisane z reguł w serwisach, nie wymyślone — tabela „co widzi każda rola"
   * na ekranie Osoby obiecuje, że „—" znaczy brak pozycji w nawigacji, a nie wyszarzony przycisk.
   */
  seenBy?: CompanyRole[];
};

/** Rola w firmie — te same wartości co w backendzie. */
type CompanyRole = 'OWNER' | 'ADMIN' | 'ACCOUNTANT' | 'EMPLOYEE' | 'BOARD_MEMBER' | 'VIEWER';

/** Księgi: pracownik ich nie prowadzi ani nie ogląda. */
const BOOKS: CompanyRole[] = ['OWNER', 'ADMIN', 'ACCOUNTANT', 'BOARD_MEMBER', 'VIEWER'];
/** Ustawienia i numeracja: właściciel, administrator, księgowa. */
const SETUP: CompanyRole[] = ['OWNER', 'ADMIN', 'ACCOUNTANT'];
/** Osoby i opłaty: tylko ci, którzy odpowiadają za firmę. */
const MANAGERS: CompanyRole[] = ['OWNER', 'ADMIN'];
type Group = { label: ((t: Dictionary) => string) | null; entries: Entry[] };

/**
 * Kolejność i przydział grup pochodzą z projektu. Grupy czyta się jak drogę dokumentu
 * przez firmę: najpierw sprzedaż, potem zakupy, potem księgi, na końcu to, co ustawia się raz.
 */
export const groups: Group[] = [
  {
    label: null,
    entries: [{ panel: 'dashboard', label: (t) => t.dashboard.open, icon: Icon.Dashboard }],
  },
  {
    label: (t) => t.shell.sales,
    entries: [
      { panel: 'invoices', label: (t) => t.invoices.open, icon: Icon.Invoice },
      { panel: 'settlements', label: (t) => t.settlements.open, icon: Icon.Balance },
      { panel: 'counterparties', label: (t) => t.counterparties.open, icon: Icon.Users },
      { panel: 'products', label: (t) => t.products.open, icon: Icon.Goods },
    ],
  },
  {
    label: (t) => t.shell.purchases,
    entries: [
      { panel: 'costs', label: (t) => t.costs.open, icon: Icon.Cost },
      { panel: 'documents', label: (t) => t.documents.open, icon: Icon.Doc },
    ],
  },
  {
    label: (t) => t.shell.books,
    entries: [
      { panel: 'chart', label: (t) => t.chart.open, icon: Icon.Book, onlyFullBooks: true, seenBy: BOOKS },
      { panel: 'journal', label: (t) => t.journal.open, icon: Icon.Book, onlyFullBooks: true, seenBy: BOOKS },
      { panel: 'ledger', label: (t) => t.ledger.open, icon: Icon.Book, seenBy: BOOKS },
      { panel: 'ryczalt', label: (t) => t.ryczalt.open, icon: Icon.Book, seenBy: BOOKS },
      { panel: 'assets', label: (t) => t.assets.open, icon: Icon.Asset, seenBy: BOOKS },
      { panel: 'inventory', label: (t) => t.inventory.open, icon: Icon.Stock, seenBy: BOOKS },
      { panel: 'reports', label: (t) => t.reports.open, icon: Icon.Balance, onlyFullBooks: true, seenBy: BOOKS },
      { panel: 'periods', label: (t) => t.periods.open, icon: Icon.Calendar, seenBy: BOOKS },
      // Sprawozdanie roczne stoi przy księgach, nie w ustawieniach: robi się je z ksiąg tego roku
      { panel: 'annual', label: (t) => t.annual.open, icon: Icon.Balance, onlyFullBooks: true, seenBy: BOOKS },
    ],
  },
  {
    label: (t) => t.shell.settlements,
    entries: [{ panel: 'billing', label: (t) => t.billing.open, icon: Icon.Fee, seenBy: MANAGERS }],
  },
  {
    label: (t) => t.shell.setup,
    entries: [
      { panel: 'settings', label: (t) => t.settings.open, icon: Icon.Gear, seenBy: SETUP },
      { panel: 'access', label: (t) => t.access.open, icon: Icon.Users, seenBy: MANAGERS },
      { panel: 'numbering', label: (t) => t.numbering.open, icon: Icon.Doc, seenBy: SETUP },
      { panel: 'mailboxConfig', label: (t) => t.documents.mailboxConfig, icon: Icon.Doc },
      { panel: 'bank', label: (t) => t.bank.open, icon: Icon.Fee },
    ],
  },
];

const DENSITY_KEY = 'firmeto-gestosc';
export type Density = 'comfortable' | 'dense';

/**
 * Gęstość układu pamiętamy między sesjami: księgowa, która raz zmieściła dwanaście wierszy
 * na ekranie, nie chce ich rozsuwać co rano. Ustawienie siedzi na `<html>`, bo dotyczy
 * także ekranów otwieranych poza powłoką.
 */
export function useDensity(): [Density, (next: Density) => void] {
  const [density, setDensity] = useState<Density>(() => {
    try {
      return window.localStorage.getItem(DENSITY_KEY) === 'dense' ? 'dense' : 'comfortable';
    } catch {
      return 'comfortable';
    }
  });

  useEffect(() => {
    document.documentElement.dataset.density = density;
    try {
      window.localStorage.setItem(DENSITY_KEY, density);
    } catch {
      // Tryb prywatny bez pamięci lokalnej — zostajemy przy wyborze na czas tej sesji
    }
  }, [density]);

  return [density, setDensity];
}

/** Przełącznik gęstości i wybór języka — stoją w pasku górnym każdego ekranu. */
export function TopControls({
  t,
  locale,
  density,
  onDensity,
  onLocale,
  children,
}: {
  t: Dictionary;
  locale: Locale;
  density: Density;
  onDensity: (next: Density) => void;
  onLocale: (next: Locale) => void;
  children?: React.ReactNode;
}) {
  return (
    <div className="sp">
      <div className="seg" role="group" aria-label={t.shell.density}>
        <button type="button" aria-pressed={density === 'comfortable'} onClick={() => onDensity('comfortable')}>
          {t.shell.comfortable}
        </button>
        <button type="button" aria-pressed={density === 'dense'} onClick={() => onDensity('dense')}>
          {t.shell.dense}
        </button>
      </div>
      <select
        className="inp w sm"
        aria-label={t.language}
        value={locale}
        onChange={(event) => onLocale(event.target.value as Locale)}
      >
        {locales.map((code) => (
          <option key={code} value={code}>
            {localeNames[code]}
          </option>
        ))}
      </select>
      {children}
    </div>
  );
}

export function Shell({
  company,
  panel,
  onPanel,
  onLeave,
  badges,
  t,
  locale,
  density,
  onDensity,
  onLocale,
  account,
  children,
}: {
  company: Company;
  panel: Panel;
  onPanel: (next: Panel) => void;
  onLeave: () => void;
  /** Liczniki przy pozycjach — tylko tam, gdzie liczbę naprawdę mamy. Zmyślony licznik jest gorszy niż jego brak. */
  badges?: Partial<Record<Panel, number>>;
  t: Dictionary;
  locale: Locale;
  density: Density;
  onDensity: (next: Density) => void;
  onLocale: (next: Locale) => void;
  account: { fullName: string; role: string };
  children: React.ReactNode;
}) {
  const current = groups.flatMap((group) => group.entries).find((entry) => entry.panel === panel);

  /*
   * Plan kont, dziennik i reszta pełnej księgowości nie mają sensu przy KPiR ani ryczałcie —
   * pozycja, która zawsze kończy się komunikatem „ta firma ma inny rodzaj ksiąg", jest ślepym
   * zaułkiem (zasada przekrojowa nr 10 z docs/04).
   *
   * Ukrywamy jednak tylko wtedy, gdy **wiemy**, że firma pełnych ksiąg nie prowadzi. Puste pole
   * znaczy „nie ma jeszcze ustawień podatkowych" albo „ustawienia zaczynają obowiązywać później"
   * — i wtedy chowanie pozycji wygląda jak brak funkcji, a nie jak brak danych. Ekran sam
   * tłumaczy, czego brakuje, i prowadzi do ustawień.
   */
  const shown = (entry: Entry) =>
    (!entry.onlyFullBooks || company.booksType == null || company.booksType === 'FULL') &&
    (!entry.seenBy || company.roles.some((role) => entry.seenBy!.includes(role as CompanyRole)));

  return (
    <div className="app">
      <aside className="side">
        <div className="brand">
          <Icon.Mark />
          <b>{t.appName}</b>
        </div>

        {/* Przełącznik firmy: nazwa i rola są kontekstem, nie treścią ekranu */}
        <button type="button" className="switch" onClick={onLeave}>
          <span className="tx">
            <b>{company.name}</b>
            <em>
              {t.companies.nip} {formatNip(company.nip)} · {account.role}
            </em>
          </span>
          <Icon.Chevron />
        </button>

        <nav aria-label={t.shell.navigation}>
          {groups.filter((group) => group.entries.some(shown)).map((group, index) => (
            <div className="navgrp" key={group.label?.(t) ?? index}>
              {group.label && <span className="lbl">{group.label(t)}</span>}
              {group.entries.filter(shown).map((entry) => (
                <button
                  type="button"
                  className="nv"
                  key={entry.panel}
                  aria-current={entry.panel === panel ? 'page' : undefined}
                  onClick={() => onPanel(entry.panel)}
                >
                  <entry.icon />
                  {entry.label(t)}
                  {badges?.[entry.panel] ? <span className="b">{badges[entry.panel]}</span> : null}
                </button>
              ))}
            </div>
          ))}
        </nav>

        <div className="me">
          <i>{initials(account.fullName)}</i>
          <span className="tx">
            <b>{account.fullName}</b>
            <em>{account.role}</em>
          </span>
        </div>
      </aside>

      <div className="main">
        <div className="top">
          <div className="crumb">
            <button type="button" className="btn ghost sm" onClick={onLeave}>
              {t.companies.title}
            </button>
            <span aria-hidden="true">›</span>
            <b>{company.name}</b>
            <span aria-hidden="true">›</span>
            <span>{current?.label(t)}</span>
          </div>
          <TopControls t={t} locale={locale} density={density} onDensity={onDensity} onLocale={onLocale} />
        </div>
        {/* Odstępy treści należą do powłoki, nie do ekranu: dzięki temu każdy ekran zaczyna się
            od nagłówka, a nie od własnego marginesu, i wszystkie stoją w jednej linii */}
        <div className="body">{children}</div>
      </div>
    </div>
  );
}
